import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft, Check, ShieldCheck, Cpu, Cloud, Database, Users, Zap, Brain, Wrench, Layers, Bot, Calendar, Globe2, Building2, Linkedin, Youtube, Instagram, Share2, Mail, Phone, MapPin, Rocket } from "lucide-react";
import axios from "axios";
import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LINKEDIN_URL = "https://www.linkedin.com/company/techshoreglobal/";
const YOUTUBE_URL = "#";
const INSTAGRAM_URL = "#";
const LOGO_IMG = "https://customer-assets.emergentagent.com/job_chat-interface-91/artifacts/cpx84h35_Techshore%20Logo%20latest.png";

/* ---------- Shared Page Shell (header + footer matching homepage) ---------- */
function PageShell({ children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-white text-ink min-h-screen flex flex-col">
      <PageNav />
      <main className="flex-1">{children}</main>
      <PageFooter />
    </div>
  );
}

function PageNav() {
  const links = [
    { to: "/services", label: "Services" },
    { to: "/crm-solutions", label: "CRM" },
    { to: "/offshore-teams", label: "Offshore" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-grey-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="page-nav-logo">
          <img src={LOGO_IMG} alt="TechShore Global — AI-led offshore teams" className="h-12 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-[13.5px] font-medium text-ink-soft hover:text-coral transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TechShore Global on LinkedIn"
            className="w-9 h-9 rounded-full border border-grey-300 hover:border-coral hover:text-coral flex items-center justify-center text-ink-soft transition"
            data-testid="page-nav-linkedin"
          >
            <Linkedin size={15} />
          </a>
          <Link to="/contact" className="btn-primary !py-2.5 !px-5 !text-[12.5px]" data-testid="page-nav-cta">
            Contact
          </Link>
        </div>
      </div>
    </header>
  );
}

function PageFooter() {
  return (
    <footer className="bg-paper border-t border-grey-200 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <Link to="/" className="inline-block mb-4">
            <img src={LOGO_IMG} alt="TechShore Global" className="h-14 w-auto" />
          </Link>
          <p className="text-[14px] text-grey-600 max-w-md leading-[1.65] font-light">
            AI-led offshore engineering partner for Australian enterprises. Building secure, ISO 27001 certified offshore IT teams from Hyderabad.
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="page-footer-social">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on LinkedIn"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="page-footer-linkedin"
            >
              <Linkedin size={18} />
            </a>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on YouTube"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="page-footer-youtube"
            >
              <Youtube size={18} />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TechShore Global on Instagram"
              className="w-11 h-11 rounded-full bg-ink text-white hover:bg-coral hover:scale-105 transition-all flex items-center justify-center"
              data-testid="page-footer-instagram"
            >
              <Instagram size={18} />
            </a>
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Company</div>
          <ul className="space-y-2 text-[14px] text-grey-700 font-light">
            <li><Link to="/services" className="hover:text-coral">Services</Link></li>
            <li><Link to="/crm-solutions" className="hover:text-coral">CRM Solutions</Link></li>
            <li><Link to="/offshore-teams" className="hover:text-coral">Offshore Teams</Link></li>
            <li><Link to="/industries" className="hover:text-coral">Industries</Link></li>
            <li><Link to="/about" className="hover:text-coral">About</Link></li>
            <li><Link to="/blog" className="hover:text-coral">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-coral">Contact</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">CRM platforms</div>
          <ul className="space-y-2 text-[14px] text-grey-700 font-light">
            <li>Salesforce</li>
            <li>Microsoft Dynamics</li>
            <li>SAP</li>
            <li>HubSpot</li>
            <li>Zoho</li>
            <li>Odoo</li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Reach</div>
          <a href="mailto:contact@techshoreglobal.com" className="text-[14px] text-grey-700 hover:text-coral block mb-1.5 font-light">contact@techshoreglobal.com</a>
          <a href="tel:+61280678518" className="text-[14px] text-grey-700 hover:text-coral block font-light">02 8067 8518</a>
          <p className="text-[13px] text-grey-600 mt-3 font-light leading-[1.6]">L24, Three International Towers<br />Barangaroo Avenue, Sydney 2000</p>
        </div>
      </div>
      <div className="border-t border-grey-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-[12px] text-grey-500 uppercase tracking-[0.1em] font-medium">
          © {new Date().getFullYear()} TechShore Global · Part of BMP Tech One Pty Ltd · ABN 40 611 334 037
        </div>
      </div>
    </footer>
  );
}

/* ---------- Services Page ---------- */
export function ServicesPage() {
  useEffect(() => {
    document.title = "Offshore IT Services Australia | Dedicated Teams, AI, Salesforce | TechShore Global";
  }, []);

  const services = [
    {
      id: "dedicated",
      icon: <Users size={24} />,
      title: "Dedicated Offshore Development Teams",
      tagline: "Your own engineering team in Hyderabad",
      bullets: [
        "Senior full-stack, backend, frontend and mobile developers",
        "QA, automation testers and DevOps engineers",
        "Pre-vetted via our 7-step screening process — top 3% only",
        "Embedded in your tools, repos, standups and SDLC",
        "Fully managed: HR, payroll, IT, compliance, security",
        "Build a team of 2 to 100+ engineers",
      ],
      desc: "Our dedicated offshore development teams in Australia deliver enterprise-grade software with senior engineers based in Hyderabad. Each team is tailored to your tech stack, agile process and product roadmap — operating as a seamless extension of your in-house team.",
    },
    {
      id: "ai",
      icon: <Brain size={24} />,
      title: "AI & Data Engineering",
      tagline: "Production AI engineers, not prototypes",
      bullets: [
        "GenAI / LLM application development (RAG, fine-tuning, evals)",
        "AI agents and intelligent automation",
        "Modern data lakehouse, dbt, real-time pipelines",
        "MLOps: feature stores, drift monitoring, scalable inference",
        "ML platform engineering on AWS, Azure and GCP",
        "Vector databases, embeddings, multimodal AI pipelines",
      ],
      desc: "Our AI engineering teams in Australia build production-grade AI features — not demos. Every engineer is screened for AI fluency, with deep experience shipping LLM apps, agents and modern data infrastructure for enterprise clients.",
    },
    {
      id: "salesforce",
      icon: <Cloud size={24} />,
      title: "Salesforce Development & Support",
      tagline: "End-to-end Salesforce expertise",
      bullets: [
        "Custom Salesforce development (Apex, LWC, Flow)",
        "Sales Cloud, Service Cloud, Marketing Cloud, Experience Cloud",
        "Integration with ERP, data warehouses and AU-specific systems",
        "Migration from legacy CRMs to Salesforce",
        "Ongoing admin support, testing and continuous improvement",
        "Salesforce-certified consultants for AU enterprise programs",
      ],
      desc: "From greenfield Salesforce implementations to ongoing managed support, our certified Salesforce consultants help Australian enterprises maximise their CRM investment.",
    },
    {
      id: "managed",
      icon: <Wrench size={24} />,
      title: "Managed IT Services",
      tagline: "Run, maintain, evolve",
      bullets: [
        "Application support and incident management",
        "Maintenance, patching and upgrades",
        "SLA-based delivery with full reporting",
        "24/7 production cover where required",
        "Cloud cost optimisation and FinOps",
        "Free your team to focus on what's next",
      ],
      desc: "Our managed IT services in Australia keep your applications running smoothly while your in-house team focuses on innovation. Predictable SLAs, transparent reporting, and senior engineers on call.",
    },
  ];

  return (
    <PageShell>
      {/* Hero */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="services-eyebrow">Services</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="services-h1">
            Offshore IT services for Australian enterprises.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            From dedicated offshore development teams to AI engineering, Salesforce support and managed IT services — we build long-term offshore capability for Australian businesses, delivered from our ISO 27001 certified centre in Hyderabad.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-20">
          {services.map((s, i) => (
            <div key={s.id} id={s.id} className="grid lg:grid-cols-12 gap-10 items-start scroll-mt-24" data-testid={`service-detail-${s.id}`}>
              <div className="lg:col-span-5">
                <div className="text-[12px] uppercase tracking-[0.2em] text-coral font-bold mb-3">0{i + 1} · {s.tagline}</div>
                <div className="w-14 h-14 rounded-xl bg-coral-tint text-coral flex items-center justify-center mb-6">{s.icon}</div>
                <h2 className="text-[32px] lg:text-[42px] leading-[1.08] tracking-[-0.025em] text-ink font-semibold">{s.title}</h2>
                <p className="mt-5 text-[16px] text-grey-700 leading-[1.7] font-light">{s.desc}</p>
                <Link to="/#contact" className="btn-primary mt-7" data-testid={`service-cta-${s.id}`}>
                  Discuss this service <ArrowRight size={16} />
                </Link>
              </div>
              <div className="lg:col-span-7">
                <ul className="space-y-3 bg-paper rounded-2xl p-7 lg:p-8 border border-grey-200">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-grey-700 font-light leading-[1.55]">
                      <span className="tick"><Check size={12} strokeWidth={3} /></span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-coral-tint border-y border-coral-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[32px] lg:text-[44px] leading-[1.1] tracking-[-0.025em] text-ink font-semibold">
            Ready to scale your engineering with offshore?
          </h2>
          <p className="mt-4 text-[16px] text-grey-700 max-w-2xl mx-auto leading-[1.6] font-light">
            Free 30-minute consultation. We'll map your needs and shortlist candidates within days.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="services-cta-book">Build your offshore team <ArrowRight size={16} /></Link>
            <Link to="/about" className="btn-outline">About TechShore <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- About Page ---------- */
export function AboutPage() {
  useEffect(() => {
    document.title = "About TechShore Global | Subsidiary of BMP Technologies | Sydney & Hyderabad";
  }, []);

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="about-eyebrow">About Us</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="about-h1">
            Australian-owned. <span className="text-coral">Globally delivered.</span>
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            TechShore Global is a subsidiary of BMP Technologies, an Australian-owned technology consulting company headquartered in Sydney with an offshore delivery centre in Hyderabad, India.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-10">
            <div data-testid="about-overview">
              <h2 className="text-[28px] lg:text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold">Who we are</h2>
              <p className="mt-4 text-[16px] text-grey-700 leading-[1.7] font-light">
                We specialise in providing AI-led offshore engineering teams, CRM expertise, and technology consulting services to organisations across Australia, New Zealand, and global markets. Our core focus is helping businesses scale efficiently by delivering high-quality, cost-effective technology talent and solutions across leading platforms including Salesforce, Microsoft Dynamics, SAP, HubSpot, Zoho, Odoo, and other enterprise systems.
              </p>
            </div>

            <div data-testid="about-strengths">
              <h2 className="text-[28px] lg:text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold">What we bring</h2>
              <ul className="mt-5 space-y-3">
                {[
                  "Local market understanding (Australia & New Zealand)",
                  "Global talent access via our Hyderabad delivery centre",
                  "Scalable offshore engagement models",
                  "Secure, ISO 27001 aligned delivery practices",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[16px] text-grey-700 font-light leading-[1.6]">
                    <span className="tick"><Check size={12} strokeWidth={3} /></span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="about-industries">
              <h2 className="text-[28px] lg:text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold">Industries we support</h2>
              <p className="mt-4 text-[16px] text-grey-700 leading-[1.7] font-light">
                TechShore Global supports organisations of all sizes across Insurance, Banking & Financial Services, Retail & eCommerce, Hospitality, Energy & Utilities, Logistics & Supply Chain, Manufacturing, and Not-for-Profit.
              </p>
            </div>

            <div data-testid="about-services">
              <h2 className="text-[28px] lg:text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold">Our services</h2>
              <ul className="mt-5 space-y-3">
                {[
                  "Dedicated offshore development teams",
                  "CRM implementation, support, and optimisation",
                  "AI & data engineering solutions",
                  "Managed IT services and ongoing support",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[16px] text-grey-700 font-light leading-[1.6]">
                    <span className="tick"><Check size={12} strokeWidth={3} /></span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="about-mission" className="bg-navy-50 rounded-2xl p-7 lg:p-8 border border-navy-100">
              <h2 className="text-[24px] lg:text-[28px] leading-[1.15] tracking-[-0.02em] text-navy font-semibold">Our mission</h2>
              <p className="mt-4 text-[17px] text-grey-700 leading-[1.7] font-light">
                To help organisations scale faster with the right talent, technology, and global delivery model.
              </p>
              <p className="mt-3 text-[15px] text-grey-600 leading-[1.65] font-light">
                At TechShore Global, we go beyond traditional outsourcing by acting as a strategic technology partner — enabling businesses to build smarter teams, improve operational efficiency, and accelerate digital transformation.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <div className="card-flat p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Sydney HQ</div>
              <p className="text-[15px] text-ink font-medium leading-[1.5]">L24, Three International Towers<br />Barangaroo Avenue, Sydney 2000</p>
            </div>
            <div className="card-flat p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Delivery centre</div>
              <p className="text-[15px] text-ink font-medium leading-[1.5]">Hyderabad, India<br />Cyberabad — the Silicon Valley of Asia</p>
            </div>
            <div className="card-flat p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Parent company</div>
              <p className="text-[15px] text-ink font-medium leading-[1.5]">BMP Tech One Pty Ltd<br />ABN 40 611 334 037</p>
            </div>
            <div className="card-flat p-7 bg-coral-tint border-coral-soft">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-2">Get in touch</div>
              <p className="text-[14.5px] text-ink leading-[1.5] font-light mb-3">Talk to a delivery lead — free 30-minute consultation.</p>
              <Link to="/contact" className="btn-primary !text-[12.5px] !py-2.5 !px-5">Build your team <ArrowRight size={14} /></Link>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- Blog data ---------- */
export const BLOG_POSTS = [
  {
    slug: "why-australian-companies-are-moving-offshore-it",
    title: "Why Australian companies are moving offshore IT in 2026",
    excerpt: "Talent shortages, rising costs, and the AI imperative are pushing Australian enterprises offshore at record rates. Here's what's driving the shift — and how to do it right.",
    date: "2026-04-20",
    readTime: "6 min read",
    keywords: "offshore IT services Australia, offshore development teams Australia, IT outsourcing India Australia",
    body: [
      { h: "Australia's tech talent crunch is structural, not cyclical", p: "Australian businesses are competing for a shrinking pool of senior engineering talent. Domestic universities produce ~7,000 IT graduates a year against demand for nearly 60,000 net new tech roles. Salaries for senior engineers in Sydney and Melbourne have climbed 18–24% over three years, while time-to-hire averages 88 days for specialist AI/ML roles. Offshore IT services in Australia are no longer a cost play — they're a capacity play." },
      { h: "AI is forcing the offshore conversation faster", p: "The rise of GenAI, LLMs and agentic systems has accelerated demand for engineers who can ship production AI. Most local engineering teams are still upskilling. Hyderabad — where we run our delivery centre — has 800K+ tech professionals and a deep concentration of AI/ML talent serving global R&D centres for Microsoft, Amazon and Google. For Australian companies, building offshore AI engineering teams is the single fastest path to AI capability." },
      { h: "Cost is still part of the story — but it's not the whole story", p: "The headline number is 40–60% savings vs local AU rates for equivalent seniority. But the real economic value comes from speed: an offshore team can typically be onboarded in 2–4 weeks vs 3–6 months for a local hire. For a single senior role, that's $80–120K of opportunity cost recovered per hire." },
      { h: "Why \"done right\" matters more than ever", p: "Offshore programs fail when they're treated as transactional staffing. They succeed when they're treated as a long-term capability investment — with proper onboarding, integrated tooling, security alignment (we run ISO 27001 certified delivery), AU timezone overlap, and engineers who stay long enough to compound expertise. Our attrition rate is under 11%, vs an industry average of ~25%." },
      { h: "The bottom line for Australian CTOs", p: "Offshore is no longer about \"finding cheaper developers.\" It's about building durable engineering capability that lets you ship faster, hire AI talent at scale, and compete with the world's best — without burning out your local team or your budget. If you're an Australian CTO, the question isn't whether to consider offshore — it's how to do it without the operational and cultural risks that gave the model a bad name a decade ago." },
    ],
  },
  {
    slug: "cost-comparison-australia-vs-offshore-teams",
    title: "Cost comparison: Australia vs offshore engineering teams (2026)",
    excerpt: "What does it actually cost to build an engineering team locally vs offshore in Hyderabad? A practical breakdown by role and seniority for Australian businesses.",
    date: "2026-04-22",
    readTime: "7 min read",
    keywords: "cost comparison offshore Australia, offshore developer rates Australia, offshore software development cost",
    body: [
      { h: "All-in cost matters, not just the rate card", p: "When Australian businesses compare offshore vs local engineering costs, they often look at hourly rates and stop. The real number to compare is fully-loaded annual cost: salary + super + leave + tooling + recruitment fees + management overhead + workspace. Once that's normalised, the offshore advantage compounds." },
      { h: "Senior full-stack engineer (5–8 yrs)", p: "In Sydney/Melbourne, a senior full-stack engineer commands AUD $160–200K base + 11.5% super + ~AUD $20K of tooling/leave/training overhead — call it AUD $200–240K all-in. The same seniority in our Hyderabad centre delivers at roughly AUD $80–110K all-in (including salary, statutory benefits, infrastructure and our managed-service margin). That's a 50–60% saving for equivalent quality." },
      { h: "AI / ML engineer", p: "AU senior AI/ML engineers are even tighter — AUD $200–280K all-in, with 90+ day time-to-hire. Hyderabad senior AI engineers (production GenAI, LLM, ML platform experience) sit in the AUD $100–140K all-in range. Offshore AI engineering teams in Australia consistently come in 50–55% cheaper, and onboard in weeks instead of months." },
      { h: "Salesforce consultant (certified, 4+ yrs)", p: "Local Salesforce consultants run AUD $150–200K all-in. Hyderabad-based certified consultants deliver the same skill at AUD $70–100K all-in. Salesforce support is one of the highest-leverage offshore plays for AU mid-market." },
      { h: "DevOps / SRE", p: "Local senior DevOps engineers cost AUD $170–220K. Offshore equivalents come in at AUD $80–110K — a 50–55% reduction with no compromise on tooling fluency (Kubernetes, Terraform, AWS/Azure/GCP)." },
      { h: "What's NOT included in offshore cost (and why it's a feature, not a bug)", p: "When you engage TechShore Global, our managed-service fee covers: HR, payroll, statutory compliance, IT, secure infrastructure, ISO 27001 controls, ongoing engineer engagement, and AU-aligned management. There are no separate line items for recruitment fees, tooling, or workspace. You see one transparent monthly invoice per engineer." },
      { h: "The real takeaway", p: "Across every senior engineering role, an offshore team in Hyderabad delivers a 40–60% all-in cost saving vs Sydney/Melbourne. For a 10-person engineering team, that's typically AUD $1.0–1.5M of annual savings — without compromising seniority, quality, security or AU alignment. That's not a margin play. That's runway." },
    ],
  },
];

/* ---------- Blog Index ---------- */
export function BlogPage() {
  useEffect(() => {
    document.title = "TechShore Global Blog | Offshore IT Insights for Australia";
  }, []);

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="blog-eyebrow">Insights</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="blog-h1">
            Offshore IT insights for Australian businesses.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            Practical guides on offshore engineering, AI capability, cost benchmarks and security — written for Australian CTOs and engineering leaders.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="card p-8 lg:p-10 group flex flex-col"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-3 mb-5 text-[12px] uppercase tracking-[0.16em] text-grey-600 font-semibold">
                <Calendar size={13} className="text-coral" />
                <span>{new Date(p.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                <span>·</span>
                <span>{p.readTime}</span>
              </div>
              <h2 className="text-[24px] lg:text-[28px] leading-[1.15] tracking-[-0.02em] text-ink font-semibold group-hover:text-coral transition-colors">
                {p.title}
              </h2>
              <p className="mt-4 text-[15px] text-grey-700 leading-[1.65] flex-1 font-light">{p.excerpt}</p>
              <div className="mt-7 pt-5 border-t border-grey-200 flex items-center justify-between">
                <span className="text-[12.5px] uppercase tracking-[0.14em] text-ink font-bold">Read article</span>
                <span className="card-arrow"><ArrowRight size={16} className="text-ink" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- Blog Post Detail ---------- */
export function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | TechShore Global`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", post.excerpt);
    }
  }, [post]);

  if (!post) {
    return (
      <PageShell>
        <section className="py-32 text-center">
          <h1 className="text-[36px] text-ink font-semibold">Article not found</h1>
          <p className="text-grey-600 mt-3 font-light">We can't find that post.</p>
          <button onClick={() => navigate("/blog")} className="btn-primary mt-6">Back to blog</button>
        </section>
      </PageShell>
    );
  }

  const shareUrl = `https://www.techshoreglobal.com/blog/${post.slug}`;
  const linkedinShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const twitterShare = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`;

  return (
    <PageShell>
      <article>
        <section className="pt-20 pb-10 lg:pt-28 bg-paper">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[13px] text-grey-600 hover:text-coral font-semibold uppercase tracking-[0.14em]" data-testid="blog-back">
              <ArrowLeft size={14} /> Back to blog
            </Link>
            <div className="flex items-center gap-3 mt-8 mb-5 text-[12px] uppercase tracking-[0.16em] text-grey-600 font-semibold">
              <Calendar size={13} className="text-coral" />
              <span>{new Date(post.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
            <h1 className="text-[36px] lg:text-[56px] leading-[1.05] tracking-[-0.03em] text-ink font-semibold" data-testid="blog-post-h1">
              {post.title}
            </h1>
            <p className="mt-6 text-[18px] text-grey-700 leading-[1.65] font-light">{post.excerpt}</p>

            {/* Share row */}
            <div className="mt-8 flex items-center gap-3" data-testid="blog-share-row">
              <span className="text-[12px] uppercase tracking-[0.16em] text-grey-600 font-bold flex items-center gap-2">
                <Share2 size={13} className="text-coral" /> Share
              </span>
              <a
                href={linkedinShare}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white hover:bg-coral transition text-[12.5px] font-semibold"
                data-testid="blog-share-linkedin"
              >
                <Linkedin size={13} /> LinkedIn
              </a>
              <a
                href={twitterShare}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-grey-300 text-ink hover:border-coral hover:text-coral transition text-[12.5px] font-semibold"
                data-testid="blog-share-twitter"
              >
                X / Twitter
              </a>
            </div>
          </div>
        </section>

        <section className="pb-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 mt-10 space-y-10">
            {post.body.map((b, i) => (
              <div key={i} data-testid={`blog-section-${i}`}>
                <h2 className="text-[24px] lg:text-[30px] leading-[1.2] tracking-[-0.02em] text-ink font-semibold">{b.h}</h2>
                <p className="mt-4 text-[16.5px] text-grey-700 leading-[1.75] font-light">{b.p}</p>
              </div>
            ))}

            <div className="mt-16 p-8 bg-coral-tint border border-coral-soft rounded-2xl">
              <h3 className="text-[22px] text-ink font-semibold leading-tight">Considering offshore for your team?</h3>
              <p className="mt-3 text-[15px] text-grey-700 leading-[1.6] font-light">
                Talk to TechShore Global — we build AI-led offshore engineering teams for Australian enterprises. Free 30-minute consultation, no pressure.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/#contact" className="btn-primary !text-[13px] !py-3 !px-6">Build your offshore team <ArrowRight size={15} /></Link>
                <Link to="/services" className="btn-outline !text-[13px] !py-3 !px-6">View services <ArrowUpRight size={15} /></Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </PageShell>
  );
}


/* ---------- CRM Solutions Page ---------- */
export function CRMSolutionsPage() {
  useEffect(() => {
    document.title = "CRM Support Services Australia | Salesforce, Dynamics, SAP, HubSpot, Zoho, Odoo";
  }, []);

  const platforms = [
    { name: "Salesforce", desc: "Sales Cloud, Service Cloud, Marketing Cloud, Experience Cloud, Apex/LWC custom development, certified consultants for AU enterprise programs.", keywords: "Salesforce support Australia, Salesforce development offshore, Salesforce consultant Australia" },
    { name: "Microsoft Dynamics", desc: "Dynamics 365 Sales, Customer Service, Marketing, Field Service, Finance & Operations. Power Platform automation and Microsoft Dynamics support for AU clients.", keywords: "Microsoft Dynamics support Australia, Dynamics 365 partner, MS Dynamics offshore" },
    { name: "SAP", desc: "SAP C/4HANA, S/4HANA CRM modules, SAP Sales Cloud, Service Cloud and Marketing Cloud. SAP CRM support and integration for enterprise AU programs.", keywords: "SAP CRM support Australia, SAP Sales Cloud, SAP offshore consulting" },
    { name: "HubSpot", desc: "HubSpot Sales, Marketing, Service Hub end-to-end implementation, automation workflows, integrations and ongoing optimisation for high-growth Australian businesses.", keywords: "HubSpot CRM support Australia, HubSpot agency partner, HubSpot offshore" },
    { name: "Zoho", desc: "Zoho CRM, Zoho One suite, Zoho Creator and analytics — implementation, customisation and Zoho CRM support for AU SMBs and enterprises.", keywords: "Zoho CRM support Australia, Zoho consultant, Zoho offshore" },
    { name: "Odoo", desc: "Open-source ERP and CRM customisation, Odoo implementation, module development and managed Odoo support tailored to your Australian operations.", keywords: "Odoo implementation Australia, Odoo consultant, Odoo offshore" },
  ];

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="crm-eyebrow">CRM Solutions</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="crm-h1">
            CRM support, development & implementation for Australia.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            Deep CRM expertise across every leading platform — Salesforce, Microsoft Dynamics, SAP, HubSpot, Zoho and Odoo. Implementation, support, optimisation and managed services delivered from our offshore centre in Hyderabad with Australian leadership and AU-aligned governance.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          {platforms.map((p, i) => (
            <div key={p.name} className="card p-8 lg:p-9 group flex flex-col" data-testid={`crm-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between mb-5">
                <h2 className="text-[26px] lg:text-[30px] leading-[1.1] tracking-[-0.02em] text-ink font-semibold">{p.name}</h2>
                <span className="text-[10.5px] uppercase tracking-[0.16em] text-coral font-bold px-3 py-1.5 bg-coral-tint rounded-full">0{i + 1}</span>
              </div>
              <p className="text-[15px] text-grey-700 leading-[1.7] flex-1 font-light">{p.desc}</p>
              <div className="mt-6 pt-5 border-t border-grey-200 flex items-center justify-between">
                <span className="text-[12px] uppercase tracking-[0.14em] text-navy-2 font-semibold">Implementation · Support · Optimisation</span>
                <Link to="/contact" className="card-arrow"><ArrowRight size={16} className="text-ink" /></Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-navy-50 border-y border-navy-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[32px] lg:text-[44px] leading-[1.1] tracking-[-0.025em] text-ink font-semibold">
            Need help with your CRM platform?
          </h2>
          <p className="mt-4 text-[16px] text-grey-700 max-w-2xl mx-auto leading-[1.6] font-light">
            Whether it's a new implementation, an audit, ongoing support or scaling an existing program — we'll match you to the right offshore team within days.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary">Build your offshore team <ArrowRight size={16} /></Link>
            <Link to="/offshore-teams" className="btn-outline">Offshore teams <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- Offshore Teams Page ---------- */
export function OffshoreTeamsPage() {
  useEffect(() => {
    document.title = "Offshore Development Teams Australia | AI Engineering | Hyderabad | TechShore Global";
  }, []);

  const roles = [
    { title: "Full-stack Developers", desc: "React, Node.js, Python, .NET, Java engineers shipping product surfaces end-to-end." },
    { title: "AI / ML Engineers", desc: "Production GenAI, LLMs, RAG, agentic systems and ML platform engineers." },
    { title: "Data Engineers", desc: "Modern lakehouse, dbt, real-time pipelines, vector DBs and analytics infrastructure." },
    { title: "DevOps / Cloud Engineers", desc: "AWS, Azure, GCP, Kubernetes, Terraform, FinOps and observability specialists." },
    { title: "QA & Automation Testers", desc: "Manual + automated testing, Playwright/Cypress, performance, security testing." },
    { title: "CRM Consultants & Developers", desc: "Salesforce, Dynamics, SAP, HubSpot, Zoho, Odoo certified consultants and developers." },
  ];

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="offshore-eyebrow">Offshore Teams</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="offshore-h1">
            Build dedicated offshore technology teams in Hyderabad.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            Reduce costs, scale faster and access experienced professionals across CRM, AI, data, cloud, development, QA and support — all delivered through our offshore centre in Hyderabad with Australian leadership and AU-aligned governance.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <h2 className="text-[32px] lg:text-[44px] leading-[1.08] tracking-[-0.025em] text-ink font-semibold">
              Senior offshore talent — across every modern stack.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="offshore-roles">
            {roles.map((r) => (
              <div key={r.title} className="card p-7" data-testid={`offshore-role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <h3 className="text-[20px] text-ink font-semibold leading-tight">{r.title}</h3>
                <p className="mt-3 text-[14.5px] text-grey-700 leading-[1.65] font-light">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h2 className="text-[32px] lg:text-[44px] leading-[1.08] tracking-[-0.025em] text-ink font-semibold">
              Why offshore development teams from Hyderabad?
            </h2>
            <ul className="mt-7 space-y-3.5">
              {[
                "40–60% cost saving vs equivalent local Australian seniority",
                "Pre-vetted senior engineers — top 3% from a deep talent pool",
                "Onboard in 2–4 weeks vs 3–6 months for local hire cycles",
                "Fully managed: HR, payroll, compliance, IT, ISO 27001 security",
                "Full Australian-hours overlap (4–6 hours daily)",
                "Under 11% engineering attrition vs 25% industry average",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15.5px] text-grey-700 font-light leading-[1.6]">
                  <span className="tick"><Check size={12} strokeWidth={3} /></span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="offshore-stats">
            {[
              { num: "40–60%", lbl: "Cost saving" },
              { num: "2–4 wk", lbl: "To onboard" },
              { num: "<11%", lbl: "Attrition" },
              { num: "100%", lbl: "AU-aligned" },
            ].map((s) => (
              <div key={s.lbl} className="card-flat p-6 text-center">
                <div className="stat-num">{s.num}</div>
                <div className="text-[12px] uppercase tracking-[0.14em] text-grey-600 mt-3 font-bold">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-coral-tint border-y border-coral-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[32px] lg:text-[44px] leading-[1.1] tracking-[-0.025em] text-ink font-semibold">
            Ready to build your offshore team?
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary">Build your offshore team <ArrowRight size={16} /></Link>
            <Link to="/services" className="btn-outline">View services <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- Industries Page ---------- */
export function IndustriesPage() {
  useEffect(() => {
    document.title = "Industries We Serve | Insurance, Banking, Retail, Energy | TechShore Global";
  }, []);

  const industries = [
    { name: "Insurance", icon: <ShieldCheck size={22} />, desc: "Claims processing, underwriting, customer experience and CRM modernisation for Australian insurers." },
    { name: "Banking & Financial Services", icon: <Building2 size={22} />, desc: "Core banking integrations, regulatory compliance, AI risk modelling and Salesforce Financial Services Cloud." },
    { name: "Retail & eCommerce", icon: <Layers size={22} />, desc: "Omnichannel platforms, POS integrations, customer data platforms and AI-driven personalisation." },
    { name: "Hospitality", icon: <Users size={22} />, desc: "Booking systems, loyalty programs, F&B operations technology and CRM for hospitality groups." },
    { name: "Energy & Utilities", icon: <Zap size={22} />, desc: "Field service management, asset monitoring, customer self-service and regulatory data platforms." },
    { name: "Logistics & Supply Chain", icon: <Globe2 size={22} />, desc: "TMS/WMS integrations, real-time tracking, route optimisation and supply-chain visibility platforms." },
    { name: "Manufacturing", icon: <Wrench size={22} />, desc: "ERP/CRM integration, IoT and shop-floor connectivity, predictive maintenance and quality systems." },
    { name: "Not-for-Profit", icon: <Rocket size={22} />, desc: "Donor management, grant administration, volunteer engagement, Salesforce Nonprofit Cloud expertise." },
  ];

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="industries-eyebrow">Industries</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="industries-h1">
            Industries we serve across Australia & New Zealand.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            We support enterprise environments across regulated, customer-facing and mission-critical industries — bringing deep CRM, AI and offshore engineering capability tuned to each sector's needs.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="industries-detail-grid">
          {industries.map((ind) => (
            <div key={ind.name} className="card p-7" data-testid={`industry-detail-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
              <div className="w-12 h-12 rounded-xl bg-coral-tint text-coral flex items-center justify-center mb-5">
                {ind.icon}
              </div>
              <h2 className="text-[22px] text-ink font-semibold leading-tight">{ind.name}</h2>
              <p className="mt-3 text-[14.5px] text-grey-700 leading-[1.65] font-light">{ind.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-navy-50 border-y border-navy-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-[15px] text-grey-700 leading-[1.7] font-light italic">
            Our team has experience supporting enterprise environments across Australia and New Zealand.
          </p>
        </div>
      </section>
    </PageShell>
  );
}

/* ---------- Contact Page ---------- */
export function ContactPage() {
  useEffect(() => {
    document.title = "Contact TechShore Global | Book a Consultation | Sydney & Hyderabad";
  }, []);

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    team_size: "", service: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus("error");
      setErrMsg("Please provide your name and email.");
      return;
    }
    setStatus("submitting");
    setErrMsg("");
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name, email: form.email,
        company: form.company || null, phone: form.phone || null,
        team_size: form.team_size || null, service: form.service || null,
        message: form.message || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <PageShell>
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6" data-testid="contact-eyebrow">Contact</div>
          <h1 className="text-[44px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-ink font-semibold max-w-4xl" data-testid="contact-h1">
            Let's build your offshore team.
          </h1>
          <p className="mt-7 text-[18px] text-grey-700 max-w-3xl leading-[1.65] font-light">
            Free 30-minute consultation. We sign NDAs. Tell us a bit about your needs and an advisor will be in touch within one business day.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div className="card-flat p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Email</div>
              <a href="mailto:contact@techshoreglobal.com" className="text-[15px] text-ink hover:text-coral inline-flex items-center gap-2 font-medium">
                <Mail size={14} /> contact@techshoreglobal.com
              </a>
            </div>
            <div className="card-flat p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Phone</div>
              <a href="tel:+61280678518" className="text-[15px] text-ink hover:text-coral inline-flex items-center gap-2 font-medium">
                <Phone size={14} /> 02 8067 8518
              </a>
            </div>
            <div className="card-flat p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Sydney HQ</div>
              <p className="text-[15px] text-ink leading-[1.55] flex items-start gap-2 font-medium">
                <MapPin size={14} className="mt-1 text-coral flex-shrink-0" />
                <span>L24, Three International Towers<br />Barangaroo Avenue, Sydney 2000</span>
              </p>
            </div>
            <div className="card-flat p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Delivery centre</div>
              <p className="text-[15px] text-ink leading-[1.55] font-medium">Hyderabad, India · Cyberabad</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {status === "success" ? (
              <div className="card-flat p-10 text-center" data-testid="contact-page-success">
                <div className="w-14 h-14 rounded-full bg-coral text-white flex items-center justify-center mx-auto">
                  <Check size={28} strokeWidth={3} />
                </div>
                <h2 className="text-[26px] text-ink mt-6 font-semibold">Got it — talk soon.</h2>
                <p className="text-[14.5px] text-grey-600 mt-3 max-w-sm mx-auto leading-[1.6] font-light">
                  Your request is in. One of our advisors will reach out within one business day.
                </p>
                <button onClick={() => setStatus("idle")} className="btn-outline mt-7">Send another <ArrowRight size={16} /></button>
              </div>
            ) : (
              <form onSubmit={submit} className="card-flat p-8 lg:p-10 grid sm:grid-cols-2 gap-5" data-testid="contact-page-form">
                <div className="sm:col-span-1"><label className="field-label">Name *</label><input className="field" value={form.name} onChange={set("name")} placeholder="Jane Smith" required data-testid="contact-form-name" /></div>
                <div className="sm:col-span-1"><label className="field-label">Email *</label><input type="email" className="field" value={form.email} onChange={set("email")} placeholder="jane@company.com" required data-testid="contact-form-email" /></div>
                <div className="sm:col-span-1"><label className="field-label">Company</label><input className="field" value={form.company} onChange={set("company")} placeholder="Acme Pty Ltd" /></div>
                <div className="sm:col-span-1"><label className="field-label">Phone</label><input className="field" value={form.phone} onChange={set("phone")} placeholder="+61 ..." /></div>
                <div className="sm:col-span-1">
                  <label className="field-label">Team size</label>
                  <select className="field" value={form.team_size} onChange={set("team_size")}>
                    <option value="">Select</option>
                    <option>1–3 engineers</option><option>4–10 engineers</option>
                    <option>11–25 engineers</option><option>25+ engineers</option>
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <label className="field-label">Capability</label>
                  <select className="field" value={form.service} onChange={set("service")}>
                    <option value="">Select</option>
                    <option>CRM (Salesforce / Dynamics / SAP / HubSpot / Zoho / Odoo)</option>
                    <option>AI / GenAI engineering</option>
                    <option>Data & ML platform</option>
                    <option>Full-stack engineering</option>
                    <option>Cloud / DevOps</option>
                    <option>Managed services</option>
                    <option>Not sure yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2"><label className="field-label">What are you trying to build?</label><textarea className="field" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your project, stack and timeline..." /></div>
                {status === "error" && (
                  <div className="sm:col-span-2 text-[13.5px] text-coral-3 bg-coral-tint border border-coral-soft rounded px-4 py-2.5 font-medium" data-testid="contact-form-error">{errMsg}</div>
                )}
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                  <p className="text-[12px] text-grey-500 max-w-sm leading-[1.5] font-light">Your data is 100% confidential. We sign NDAs.</p>
                  <button type="submit" disabled={status === "submitting"} className="btn-primary" data-testid="contact-form-submit">
                    {status === "submitting" ? "Sending..." : "Speak with an expert"} <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
